import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Copy, Check, Twitter } from 'lucide-react';

const ExcuseGeneratorPage: React.FC = () => {
  const [excuse, setExcuse] = useState('');
  const [severity, setSeverity] = useState(50);
  const [blameTarget, setBlameTarget] = useState('solar-flares');
  const [technicalDepth, setTechnicalDepth] = useState(50);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Excuse components
  const prefixes = [
    "The cascading failure originated in",
    "An unexpected interaction between",
    "The root cause was traced to",
    "A critical misconfiguration in",
    "The incident began when",
    "Investigation revealed issues with",
    "The outage stemmed from",
    "Analysis indicates problems in",
    "The system degradation started in",
    "Diagnostics point to failures in"
  ];

  const systems = [
    "the legacy DNS infrastructure",
    "the load balancer cluster",
    "Server Rack #7's management port",
    "the backup replication system",
    "the distributed cache layer",
    "the authentication middleware",
    "the database connection pool",
    "the message queue broker",
    "the CDN edge nodes",
    "the monitoring stack itself"
  ];

  const connectors = [
    "when",
    "after",
    "coinciding with",
    "during",
    "immediately following",
    "as a result of",
    "triggered by",
    "exacerbated by",
    "combined with",
    "in conjunction with"
  ];

  const blameReasons: { [key: string]: string[] } = {
    'solar-flares': [
      "a solar flare",
      "cosmic ray interference",
      "geomagnetic disturbances",
      "solar wind activity",
      "space weather anomalies"
    ],
    'users': [
      "a user attempting to print to a network switch",
      "someone clicking 'yes' on every dialog box",
      "a user who thought sudo meant 'super do'",
      "an executive running .exe attachments",
      "users treating the server room as storage"
    ],
    'pfy': [
      "the PFY's experimental configuration",
      "the PFY believing the documentation",
      "the PFY's creative interpretation of 'restart'",
      "the PFY discovering the sudo command",
      "the PFY's first attempt at automation"
    ],
    'vendor': [
      "a vendor firmware update",
      "undocumented vendor API changes",
      "vendor support being 'unavailable'",
      "the vendor's definition of 'compatible'",
      "vendor-supplied drivers from 2007"
    ],
    'legacy': [
      "legacy systems from the previous decade",
      "code that predates version control",
      "systems held together with hopes and shell scripts",
      "infrastructure that refuses to die",
      "the system nobody dares to touch"
    ],
    'cosmic-rays': [
      "cosmic rays flipping a critical bit",
      "quantum tunneling in the storage medium",
      "spontaneous bit rot",
      "subatomic particle interference",
      "quantum mechanics being unhelpful"
    ]
  };

  const technicalDetails = {
    vague: [
      "The issue is being investigated.",
      "We're monitoring the situation.",
      "Engineers are aware.",
      "The system is under observation.",
      "We're looking into it."
    ],
    medium: [
      "resulting in elevated error rates and degraded performance",
      "causing intermittent connectivity issues across multiple zones",
      "leading to timeout exceptions in the application layer",
      "triggering automatic failover procedures",
      "manifesting as sporadic 503 responses"
    ],
    specific: [
      "causing a race condition in the mutex implementation that corrupted the shared memory segment",
      "resulting in a 47% increase in p99 latency and eventual connection pool exhaustion",
      "triggering a cascading failure that propagated through 3 availability zones",
      "inducing a memory leak in the garbage collector itself, which is ironic",
      "creating a deadlock between the cache invalidation and session replication threads"
    ]
  };

  const conclusions = [
    "The PFY is investigating.",
    "We're implementing additional monitoring.",
    "A post-mortem will be scheduled.",
    "The incident has been documented for posterity.",
    "Management has been... informed.",
    "The Operator is considering coffee.",
    "Resolution timeline: when the stars align.",
    "ETA: approximately 'soon'.",
    "The situation is evolving.",
    "Updates will follow when convenient."
  ];

  const generateExcuse = () => {
    setIsGenerating(true);
    setCopied(false);

    // Simulate generation delay for effect
    setTimeout(() => {
      const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
      const system = systems[Math.floor(Math.random() * systems.length)];
      const connector = connectors[Math.floor(Math.random() * connectors.length)];
      const blame = blameReasons[blameTarget][Math.floor(Math.random() * blameReasons[blameTarget].length)];

      let technical = '';
      if (technicalDepth < 33) {
        technical = technicalDetails.vague[Math.floor(Math.random() * technicalDetails.vague.length)];
      } else if (technicalDepth < 66) {
        technical = technicalDetails.medium[Math.floor(Math.random() * technicalDetails.medium.length)];
      } else {
        technical = technicalDetails.specific[Math.floor(Math.random() * technicalDetails.specific.length)];
      }

      const conclusion = conclusions[Math.floor(Math.random() * conclusions.length)];

      const generatedExcuse = `${prefix} ${system} ${connector} ${blame}${technicalDepth > 33 ? ', ' + technical : ''}. ${conclusion}`;

      setExcuse(generatedExcuse);
      setIsGenerating(false);
    }, 800);
  };

  const copyToClipboard = async () => {
    if (excuse) {
      await navigator.clipboard.writeText(excuse);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const tweetExcuse = () => {
    const tweetText = `"${excuse}" — courtesy of The Operator's Excuse Generator`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(tweetUrl, '_blank');
  };

  // Sample excuses
  const sampleExcuses = [
    "Quantum tunneling in the SSD caused bit rot in the hypervisor. It's a known issue. In theoretical physics.",
    "The load balancer achieved sentience and made different choices. We're negotiating.",
    "Cosmic rays flipped a bit. That bit was important. Very important.",
    "The DNS server was attempting to resolve 'localhost.local' recursively. For 6 hours.",
    "A user tried to email the server. The server took it personally."
  ];

  const [currentSample, setCurrentSample] = useState(0);
  const refreshSamples = () => {
    setCurrentSample((prev) => (prev + 1) % sampleExcuses.length);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Page Header */}
          <div className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/tool-icon-excuse-generator.png"
                alt="Excuse Generator"
                className="w-24 h-24 object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              The Excuse Generator
            </h1>
            <p className="text-xl text-gray-400 mb-4">
              Never be at a loss for a plausible explanation again.
            </p>
            <p className="text-gray-300 max-w-2xl mx-auto leading-relaxed">
              One of The Operator's most beloved tools. When something breaks (and it will),
              you need an excuse that's technical enough to sound real but absurd enough to be entertaining.
            </p>
            <p className="text-gray-400 mt-2">
              This is that tool.
            </p>
          </div>

          {/* Control Panel */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-100 mb-6">Control Panel</h2>

            <div className="space-y-6">
              {/* Severity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Severity Level: <span className="text-red-500 font-mono">{severity}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={severity}
                  onChange={(e) => setSeverity(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Minor</span>
                  <span>Catastrophic</span>
                </div>
              </div>

              {/* Blame Target */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Blame Target
                </label>
                <select
                  value={blameTarget}
                  onChange={(e) => setBlameTarget(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="solar-flares">Solar flares</option>
                  <option value="users">Users</option>
                  <option value="pfy">The PFY</option>
                  <option value="vendor">Vendor</option>
                  <option value="legacy">"Legacy systems"</option>
                  <option value="cosmic-rays">Cosmic rays</option>
                </select>
              </div>

              {/* Technical Depth */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Technical Depth: <span className="text-teal-400 font-mono">{technicalDepth}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={technicalDepth}
                  onChange={(e) => setTechnicalDepth(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Vague</span>
                  <span>Incomprehensibly specific</span>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateExcuse}
                disabled={isGenerating}
                className="w-full btn btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    GENERATE EXCUSE
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Output Display */}
          {excuse && (
            <div className="bg-black border border-red-500/50 rounded-lg p-8 mb-8 animate-fadeIn">
              <h3 className="text-sm font-bold text-red-500 uppercase tracking-wide mb-4">
                Generated Excuse:
              </h3>
              <p className="text-gray-100 text-lg leading-relaxed mb-6 font-mono">
                {excuse}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={copyToClipboard}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy to Clipboard
                    </>
                  )}
                </button>
                <button
                  onClick={generateExcuse}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Generate Another
                </button>
                <button
                  onClick={tweetExcuse}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Twitter className="w-4 h-4" />
                  Tweet This
                </button>
              </div>
            </div>
          )}

          {/* Sample Excuses */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 mb-12">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-100">
                Sample Excuses
              </h3>
              <button
                onClick={refreshSamples}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Refresh samples"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
            <ul className="space-y-3">
              {sampleExcuses.map((sample, index) => (
                <li
                  key={index}
                  className={`text-gray-400 leading-relaxed pl-4 border-l-2 transition-opacity ${
                    index === currentSample ? 'border-red-500 text-gray-300' : 'border-gray-700'
                  }`}
                >
                  "{sample}"
                </li>
              ))}
            </ul>
          </div>

          {/* Ethics Banner */}
          <div className="ethics-banner">
            <div className="text-center">
              <p className="text-sm text-gray-300 leading-relaxed">
                ⚠️ This is a <strong>fictional, satirical tool</strong> for entertainment only.
                Do not use for actual incident reports (please). All excuses are procedurally generated nonsense.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ExcuseGeneratorPage;
